import React from 'react';
import PropTypes from 'prop-types';
import LayoutPlaceholderCard from '../../../../components/shared/LayoutPlaceholderCard';
import { getCardPlaceholderMeta } from '../../../../utils/getCardDisplayTitle';
import { getHospitalLayoutSkeletonVariant } from '../../../../utils/hospitalLayoutPlaceholderSkeleton';
import { getLayoutPreviewSharePanelProps } from '../../../../utils/layoutPreviewShareBadge';
import {
	arePreviewPropsEqual,
	CombineTotalPreviewBody,
	DaysPreviewBody,
	FloorsPreviewBody,
	InsurancePreviewBody,
	NinetyDaysPreviewBody,
	resolveDynamicPreviewBody,
	UserListPreviewBody,
} from '../../../../components/shared/layoutPreview';

/**
 * Layout-mode preview renderer for the Overall page.
 *
 * See `components/shared/layoutPreview/` for the shared primitives. This
 * file just maps each Overall-specific `card.component` value to the
 * matching shared body, or delegates to the dynamic resolver.
 *
 * `exhaustInsuranceData` renders `DynamicDataCardTotalCard` (the same
 * gradient "big number + sub-totals" component dynamic combine-tabs use),
 * so it reuses `CombineTotalPreviewBody`.
 */
const PREVIEW_BY_COMPONENT = {
	doctor: UserListPreviewBody,
	floors: FloorsPreviewBody,
	insurance: InsurancePreviewBody,
	days: DaysPreviewBody,
	ninetyDays: NinetyDaysPreviewBody,
	exhaustInsuranceData: CombineTotalPreviewBody,
};

const OverallCardLayoutPreview = ({ card, dynamicCards, sharedUsersByTabId }) => {
	const { title: placeholderTitle } = getCardPlaceholderMeta(card, {
		dynamicCards,
	});

	let PreviewBody = null;
	let previewBodyProps = {};
	if (card?.component === 'dynamic') {
		PreviewBody = resolveDynamicPreviewBody(card?.data);
		previewBodyProps = { role: 'generic', useGenericLabels: true };
	} else if (card?.component) {
		PreviewBody = PREVIEW_BY_COMPONENT[card.component] || null;
		if (card.component === 'doctor') {
			previewBodyProps = { role: 'doctor' };
		}
	}

	return (
		<LayoutPlaceholderCard
			variant="card"
			title={placeholderTitle}
			width={card?.width}
			sharePanel={getLayoutPreviewSharePanelProps(card, { sharedUsersByTabId })}
			skeletonVariant={getHospitalLayoutSkeletonVariant(card)}
			body={PreviewBody ? <PreviewBody {...previewBodyProps} /> : null}
		/>
	);
};

OverallCardLayoutPreview.propTypes = {
	card: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		component: PropTypes.string,
		type: PropTypes.string,
		width: PropTypes.number,
		data: PropTypes.object,
	}).isRequired,
	dynamicCards: PropTypes.array,
	sharedUsersByTabId: PropTypes.object,
};

OverallCardLayoutPreview.defaultProps = {
	dynamicCards: [],
	sharedUsersByTabId: undefined,
};

export default React.memo(OverallCardLayoutPreview, arePreviewPropsEqual);
